import type { LeadFormData, LeadResponse, SelectedFilters } from "./types";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export function captureUtmParams(): void {
  if (typeof window === "undefined") return;

  try {
    const urlParams = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((key) => {
      const value = urlParams.get(key);
      if (value) {
        sessionStorage.setItem(key, value);
      }
    });
  } catch {
    // ignore
  }
}

function getSavedUtms() {
  if (typeof window === "undefined") {
    return {
      UTM_SOURCE: "",
      UTM_MEDIUM: "",
      UTM_CAMPAIGN: "",
      UTM_CONTENT: "",
      UTM_TERM: "",
    };
  }

  return {
    UTM_SOURCE: sessionStorage.getItem("utm_source") || "",
    UTM_MEDIUM: sessionStorage.getItem("utm_medium") || "",
    UTM_CAMPAIGN: sessionStorage.getItem("utm_campaign") || "",
    UTM_CONTENT: sessionStorage.getItem("utm_content") || "",
    UTM_TERM: sessionStorage.getItem("utm_term") || "",
  };
}

export function normalizePhoneDigits(phoneStr: string): string {
  let digits = phoneStr.replace(/\D/g, "");
  if (digits.startsWith("8")) {
    digits = "7" + digits.slice(1);
  }
  if (digits.length === 10) {
    digits = "7" + digits;
  }
  return digits;
}

function extractNumber(value: string): string {
  const match = value.match(/[\d.]+/);
  if (!match) return "";
  const num = parseFloat(match[0]);
  return Number.isInteger(num) ? String(num) : String(num);
}

function buildFormPayload(
  data: LeadFormData,
  selected: SelectedFilters,
) {
  const { name, phone, item, additionalNotes } = data;
  const utmParams = getSavedUtms();

  if (item) {
    return {
      name,
      phone: normalizePhoneDigits(phone),
      notes: additionalNotes.trim() || "Запрос расчета стоимости и доставки",
      brand: item.brand,
      model: item.baseModel || item.model,
      engine_code: item.engineCode,
      fuel: item.fuel,
      displ: extractNumber(item.displacement),
      power: extractNumber(item.power),
      trtype: item.transmissionType || "",
      trcode: item.transmissionCode || "",
      priceets: item.priceEst || "",
      donorvin: item.donorVin || "",
      oem: item.oem || "",
      category: item.category,
      ...utmParams,
    };
  }

  return {
    name,
    phone: normalizePhoneDigits(phone),
    notes: additionalNotes.trim() || "Запрос расчета стоимости и доставки",
    brand: selected.brand,
    model: selected.model,
    engine_code: "",
    fuel: selected.fuel,
    displ: extractNumber(selected.displacement),
    power: extractNumber(selected.power),
    trtype: "",
    trcode: "",
    priceets: "",
    donorvin: "",
    oem: "",
    category: "Подбор ДВС",
    ...utmParams,
  };
}

export async function createLead(
  data: LeadFormData,
  selected: SelectedFilters,
): Promise<LeadResponse> {
  const payload = buildFormPayload(data, selected);

  try {
    const response = await fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const jsonResult = (await response.json()) as LeadResponse;

    if (response.ok && jsonResult.success) {
      return jsonResult;
    }

    return {
      success: false,
      error: jsonResult.error || "Ошибка отправки заявки",
    };
  } catch {
    return {
      success: false,
      isNetworkOrCors: true,
      error: "Не удалось подключиться к серверу заявок.",
    };
  }
}

export function isValidRussianPhone(phoneStr: string): boolean {
  if (!phoneStr) return false;
  const digits = normalizePhoneDigits(phoneStr);
  return (
    digits.length === 11 &&
    digits.startsWith("7") &&
    ["9", "8", "4", "3"].includes(digits[1])
  );
}

export function applyPhoneMask(inputVal: string): string {
  let digits = inputVal.replace(/\D/g, "");

  if (!digits) return "";

  if (digits[0] === "8" || digits[0] === "7") {
    digits = digits.substring(1);
  }

  digits = digits.substring(0, 10);

  let formatted = "+7";
  if (digits.length > 0) {
    formatted += " (" + digits.substring(0, 3);
  }
  if (digits.length >= 3) {
    formatted += ") " + digits.substring(3, 6);
  }
  if (digits.length >= 6) {
    formatted += "-" + digits.substring(6, 8);
  }
  if (digits.length >= 8) {
    formatted += "-" + digits.substring(8, 10);
  }

  return formatted;
}
